import { memo, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';

import { Box, useTheme } from '@/shared/ui';

import type { Sx } from '@/shared/types/uiTypes';

import type { CatalogItem } from '@/entities/catalog-item';
import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol';
import { isSameSymbol, isSameSymbolIgnoringPeriod } from '@/entities/symbol';
import { usePermission } from '@/features/_shared/user';
import {
  requestForwardCurveSelection,
  requestSpotSelection,
  SELECTOR_MODES
} from '@/features/symbol-selector';
import { shallowEqual, useAppDispatch, useAppSelector } from '@/store/hooks';

import { isRowRestricted } from '../../../lib/isRowRestricted';
import { parseForwardCurveRowToSymbol } from '../../../lib/parseForwardCurveRowToSymbol';
import { COLLAPSED_ROW_HEIGHT, EXPANDED_ROW_BG } from '../constants';
import ForwardCatalogCell from './ForwardCatalogCell/ForwardCatalogCell';
import PeriodDetailPanel from './PeriodDetailPanel/PeriodDetailPanel';
import SpotCatalogCell from './SpotCatalogCell/SpotCatalogCell';

import type { Cell, Row, VisibilityState } from '@tanstack/react-table';

const computeForwardSelection = (
  rowSymbols: SymbolKey[],
  selected: SymbolKey[],
  equals: (a: SymbolKey, b: SymbolKey) => boolean
) => {
  let any = false;
  let all = true;
  for (const sym of rowSymbols) {
    let found = false;
    for (const s of selected) {
      if (equals(s, sym)) {
        found = true;
        break;
      }
    }
    if (found) any = true;
    else all = false;
  }
  return { any, all };
};

const rowWrapperSx = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%'
} satisfies Sx;

// Horizontal rules above and below the whole expanded block (main row + period
// panel) so it reads as one framed unit against the zebra rows. Drawn as
// absolutely-positioned pseudo-elements rather than a border: a real border
// (border-box) steals 1px off the top and nudges the row's content down as it
// opens, and an inset shadow would be hidden behind the children's opaque
// backgrounds. A positioned overlay paints on top of the children and never
// affects layout, so it stays visible and nothing shifts.
const expandedRule = {
  content: '""',
  position: 'absolute' as const,
  left: 0,
  right: 0,
  height: '1px',
  backgroundColor: 'gx.border',
  pointerEvents: 'none' as const
};

const expandedWrapperSx = {
  '&::before': { ...expandedRule, top: 0 },
  '&::after': { ...expandedRule, bottom: 0 }
} satisfies Sx;

// When the row directly above is also expanded, its bottom rule already draws the
// shared divider — dropping our top rule collapses the pair to a single 1px line.
const expandedWrapperCollapsedTopSx = {
  '&::after': { ...expandedRule, bottom: 0 }
} satisfies Sx;

const catalogRowSx = {
  display: 'flex',
  alignItems: 'center',
  height: `${COLLAPSED_ROW_HEIGHT}px`,
  width: '100%',
  padding: 0,
  paddingLeft: '8px',
  '&[data-restricted="false"]:hover': {
    backgroundColor: 'table.highlight'
  }
} satisfies Sx;

type CatalogTableRowProps = Readonly<{
  rowIndex: number;
  rowStart: number;
  row: Row<CatalogItem>;
  isForwardCurves: boolean;
  isInDashboard: boolean;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
  canExpand: boolean;
  isExpanded: boolean;
  isFullyExpanded: boolean;
  periods?: CatalogItem[];
  onToggleExpand: (code: string) => void;
  onShowAll: (code: string) => void;
  measureRef: (node: Element | null) => void;
  // Drives `row.getVisibleCells()` and the period sub-table's visible columns.
  // Its identity changes only when column visibility changes, so it doubles as
  // the memo's re-render signal (the stable `row` reference alone would not
  // trigger one) while letting the memo hold across scroll, where only
  // `rowStart`/`rowIndex` matter.
  columnVisibility: VisibilityState;
  // True when the row directly above is also expanded, so this block drops its
  // top rule and the pair shares a single 1px divider instead of stacking 2px.
  collapseTopBorder?: boolean;
}>;

function CatalogTableRow({
  rowIndex,
  rowStart,
  row,
  isForwardCurves,
  isInDashboard,
  renderFactsheetButton,
  canExpand,
  isExpanded,
  isFullyExpanded,
  periods,
  onToggleExpand,
  onShowAll,
  measureRef,
  columnVisibility,
  collapseTopBorder
}: CatalogTableRowProps) {
  const { palette } = useTheme();
  const { hasPermission } = usePermission();
  const isPermissionGranted = hasPermission(Permission.ViewCatalogDetails);
  const code = row.original.Code;

  const isRestrictedData = useMemo(
    () => isRowRestricted(row, isForwardCurves, isPermissionGranted),
    [row, isForwardCurves, isPermissionGranted]
  );

  const symbol = useMemo(() => row.getValue<SymbolKey | undefined>('Symbol'), [row]);

  const symbols = useMemo((): SymbolKey[] => {
    if (!isForwardCurves) return [];
    return parseForwardCurveRowToSymbol(row);
  }, [isForwardCurves, row]);

  // Single selector for all selection state — one Redux subscription per row instead of
  // four, and avoids the stale-closure hazard of closing over derived values across calls.
  const { isSpotSelected, isAnyForwardSelected, isAllForwardSelected, isForwardCurveLocked } =
    useAppSelector(state => {
      const { symbols: selected, selectorMode } = state.symbolSelector;
      const forwardMode = selectorMode === SELECTOR_MODES.FORWARD_CURVE;
      const equals = forwardMode ? isSameSymbolIgnoringPeriod : isSameSymbol;

      const spotSelected = symbol ? selected.some(s => equals(s, symbol)) : false;

      let anyForward = false;
      let allForward = false;
      if (isForwardCurves && symbols.length > 0) {
        const fwd = computeForwardSelection(symbols, selected, equals);
        anyForward = fwd.any;
        allForward = fwd.all;
      }

      return {
        isSpotSelected: spotSelected,
        isAnyForwardSelected: anyForward,
        isAllForwardSelected: allForward,
        isForwardCurveLocked: selectorMode === SELECTOR_MODES.SPOT
      };
    }, shallowEqual);

  const backgroundColor = useMemo(() => {
    if (isForwardCurves) {
      if (isAnyForwardSelected) return palette.table.forwardCurve;
    } else if (isSpotSelected) {
      return palette.table.spot;
    }
    if (isRestrictedData) return palette.table.infoRowBg;
    // An expanded row merges with its period panel below into one uniform block.
    if (isExpanded) return EXPANDED_ROW_BG;
    return rowIndex % 2 === 0 ? palette.gx.tileLight : palette.gx.tileDark;
  }, [
    isForwardCurves,
    isAnyForwardSelected,
    isSpotSelected,
    isRestrictedData,
    isExpanded,
    rowIndex,
    palette
  ]);

  const dispatch = useAppDispatch();

  const handleRowClick = useCallback(() => {
    if (isRestrictedData) return;
    if (isForwardCurves) {
      if (!symbols.length) return;
      dispatch(requestForwardCurveSelection({ symbols }));
    } else if (symbol) {
      dispatch(requestSpotSelection({ symbol }));
    }
  }, [dispatch, isForwardCurves, isRestrictedData, symbol, symbols]);

  // Stable identity (code is fixed per row, onShowAll is memoized upstream) so it
  // doesn't break the memoized PeriodDetailPanel.
  const handleShowAll = useCallback(() => onShowAll(code), [onShowAll, code]);

  const renderCell = (cell: Cell<CatalogItem, unknown>) => {
    const cellKey = `${row.id}-${cell.id}`;
    if (isForwardCurves) {
      return (
        <ForwardCatalogCell
          key={cellKey}
          cell={cell}
          firstSelected={isAnyForwardSelected}
          isForwardCurveLocked={isForwardCurveLocked}
          isInDashboard={isInDashboard}
          isRestrictedData={isRestrictedData}
          renderFactsheetButton={renderFactsheetButton}
          secondSelected={isAllForwardSelected}
          symbols={symbols}
        />
      );
    }
    return (
      <SpotCatalogCell
        key={cellKey}
        canExpand={canExpand}
        cell={cell}
        isExpanded={isExpanded}
        isInDashboard={isInDashboard}
        isRestrictedData={isRestrictedData}
        onToggleExpand={onToggleExpand}
        renderFactsheetButton={renderFactsheetButton}
        symbol={symbol}
      />
    );
  };

  return (
    <Box
      ref={measureRef}
      data-index={rowIndex}
      sx={
        isExpanded
          ? [rowWrapperSx, collapseTopBorder ? expandedWrapperCollapsedTopSx : expandedWrapperSx]
          : rowWrapperSx
      }
      style={{ transform: `translateY(${rowStart}px)` }}
    >
      <Box
        data-restricted={String(isRestrictedData)}
        sx={[catalogRowSx, { backgroundColor }]}
        style={{ cursor: isRestrictedData ? 'not-allowed' : 'pointer' }}
        onClick={handleRowClick}
      >
        {row.getVisibleCells().map(renderCell)}
      </Box>
      {isExpanded && periods && (
        <PeriodDetailPanel
          columnVisibility={columnVisibility}
          isInDashboard={isInDashboard}
          periods={periods}
          renderFactsheetButton={renderFactsheetButton}
          showAll={isFullyExpanded}
          onShowAll={handleShowAll}
        />
      )}
    </Box>
  );
}

export default memo(CatalogTableRow);
